import { FormStatuses } from "./index";

/**
 * createForm initializes a new form with the given form.
 */
export const createForm = ({ getters, commit }, { name, fields }) => {
  if (!name || !name.trim()) {
    throw new Error(`Form name must not be empty!`);
  }
  if (getters.formExists({ form: name })) {
    return;
  }

  commit("createForm", { name, fields });
};

/**
 * deleteForm ensures a form is purged from the store.
 */
export const deleteForm = ({ getters, commit }, form) => {
  if (!form || !getters.formExists({ form })) {
    return;
  }

  commit("deleteForm", form);
};

/**
 * clearForm returns a form to it's initial state.
 */
export const clearForm = ({ state, getters, dispatch }, form) => {
  if (!form || !form.trim()) {
    throw new Error(`Form name must not be empty!`);
  }
  if (!getters.formExists({ form })) {
    return;
  }

  const index = getters.getFormIndex({ form });
  dispatch("setFields", {
    form,
    fields: state.forms[index].original
  });
};

/**
 * setFormState validates and sets the current state of the form.
 */
export const setStatus = ({ getters, commit }, { form, status }) => {
  if (!form || !form.trim()) {
    throw new Error(`form name must not be empty!`);
  }

  if (!getters.formExists({ form })) {
    throw new Error(`cannot set state on unknown form ${form}`);
  }

  if (!FormStatuses.includes(status)) {
    throw new Error(`cannot set form to invalid status ${status}.`);
  }

  const index = getters.getFormIndex({ form });
  commit("setStatus", { index, status });
};

/**
 * setField sets the value of a field in the form.
 */
export const setField = ({ getters, commit }, { form, field, value }) => {
  if (!form || !form.trim()) {
    throw new Error(`form name must not be empty!`);
  }

  if (!getters.formExists({ form })) {
    throw new Error(`cannot set field on unknown form ${form}`);
  }

  if (!getters.fieldExists({ form, field })) {
    console.warn(`field '${field}' does not exist on form ${form}`);
    return;
  }

  const index = getters.getFormIndex({ form });
  commit("setField", {
    index,
    field,
    value
  });
};

/**
 * setFields is a shortcut to set many fields
 */
export const setFields = ({ dispatch }, { form, fields }) => {
  Object.keys(fields).forEach(key => {
    dispatch("setField", {
      form,
      field: key,
      value: fields[key]
    });
  });
};


/**
 * setDisabledFields is a shortcut to set disabled status on many fields
 */
export const setDisabledFields = ({ dispatch }, { form, fields }) => {
  for (const field in fields) {
    dispatch("setFieldIsDisabled", {
      form,
      field,
      value: fields[field],
    });
  }
};

/**
 * setErrors parses and sets the errors for form.
 */
export const setErrors = ({ getters, commit }, { form, errors }) => {
  if (!form || !form.trim()) {
    throw new Error(`form name must not be empty!`);
  }

  if (!getters.formExists({ form })) {
    throw new Error(`cannot set errors for undefined form ${form}`);
  }

  // This weird error check is due to some odd laravel validation behavior.
  // When you have a validation rule like `fieldName.*`, which
  // accepts an array of possible values, the error response
  // will look something like `fieldName.0`, keying in the index
  // of the item with the error. This is theoretically useful,
  // but in practice is really awkward to work with. For now
  // this bit of code normalizes the error message to just `fieldName`
  // so it can be worked with like all other fields.
  // - Tony B
  let errs = errors;
  Object.keys(errs).forEach(field => {
    const segs = field.split(".");
    if (segs.length < 1) {
      return;
    }

    errs[segs[0]] = errs[field];
  });

  const index = getters.getFormIndex({ form });
  commit("setErrors", {
    index,
    errors: errs
  });
};


/**
 * setFieldIsDisabled sets whether the given field name is disabled using a truthy value.
 */
export const setFieldIsDisabled = ({ getters, commit }, { form, field, value }) => {
  if (!form || !form.trim()) {
    throw new Error(`form name must not be empty!`);
  }

  if (!getters.formExists({ form })) {
    throw new Error(`cannot set field on unknown form ${form}`);
  }

  if (!getters.fieldExists({ form, field })) {
    console.warn(`field '${field}' does not exist on form ${form}`);
    return;
  }

  const index = getters.getFormIndex({ form });
  commit("setDisabled", {
    index,
    field,
    value,
  });
};
